/**
 * The nominations grid — turn `13a`, from the canvas.
 *
 * This replaces a seven-column table. The drawing is explicit about why: the farm auction is the
 * draft board with a different unit, so the nominations get the same object — a 34px round gutter,
 * ten `minmax(0,1fr)` columns, one row per round, serpentine — and a sold cell wears the violet
 * frame that means farm everywhere else in the app. `src/ui/BoardGrid.tsx` is the model, and the
 * name ladder and the pale position chip are shared with it rather than re-derived.
 *
 * The three drawn states, and one that is ours:
 *
 *   sold      violet 2px frame, tinted in the WINNER's hue — not the nominator's, which is the
 *             one thing the drawing's own legend spells out. Player, position, the winning badge
 *             and price, and the turn code.
 *   open      the accent frame, the pulsing dot and the sheen, exactly as the board's clock cell:
 *             this is the lot being bid on right now.
 *   upcoming  dashed grey, naming whose nomination it is — the question the grid is asked between
 *             lots is "who is up next", and a bare code does not answer it.
 *   passed    OURS. The drawing has no passed turn in it and the log has had them since 2021; a
 *             pass is a recorded decision, not an empty cell, so it is drawn as resolved-but-grey
 *             rather than as still to come. Written up in `design-conformance.md`.
 */

import {
  nomGrid, nomSoldCount, type NomCell, type Turn,
} from '../domain/farmAuction.js';
import { faab } from '../domain/faab.js';
import { managerShort } from '../domain/identity.js';
import { hueFor } from './GmBadge.js';
import { nameFit, splitName } from './BoardGrid.js';

interface Props {
  turns: readonly Turn[];
  order: readonly string[];
  /** The manager looking at the screen — his column heading takes the accent, as on the board. */
  me?: string;
}

export function NomGrid({ turns, order, me }: Props) {
  const teams = order.length;
  const rows = nomGrid(turns, teams);
  const { sold, total } = nomSoldCount(turns);

  return (
    <div className="nboardwrap">
      <div className="nboard">
        <div className="dbrow dbhead">
          <div />
          {order.map((g, i) => (
            <div className={`dbgm ${me && g.toUpperCase() === me.toUpperCase() ? 'me' : ''}`}
              key={g} data-seat={i + 1}>
              <span className="nm">{managerShort(g)}</span>
            </div>
          ))}
        </div>

        {rows.map((cells, i) => (
          <div className="dbrow" key={i}>
            <div className="dbrd">
              <b>{i + 1}</b>
              <i aria-hidden="true">{(i + 1) % 2 === 1 ? '→' : '←'}</i>
            </div>
            {cells.map((c, j) => (
              c ? <Cell key={c.code} c={c} /> : <div className="ncell upcoming" key={j} />
            ))}
          </div>
        ))}
      </div>

      {/* The drawing's legend, in the drawing's words. */}
      <div className="dblegend nlegend">
        <span className="key">
          <i className="sw nsold" />sold — tinted for the winner, not the nominator
        </span>
        <span className="key"><i className="sw nnext" />upcoming, with whose nomination it is</span>
        <span className="key"><i className="sw npassed" />passed</span>
      </div>

      {/*
        THE ROUND COUNT IS READ, NOT WRITTEN. It used to say "Two rounds" in prose beside a grid
        that draws however many rounds it is given, so setting the count on the configuration
        table would have left the sentence underneath it lying. The lapse warning only appears
        while there is something to lapse.
      */}
      <p className="note nomfoot">
        {total} nominations, {sold} sold.
        {rows.length === 2 && (
          <> Two rounds is the 2025 experiment and is <b>conditional</b> — it lapses unless the
            committee re-votes it on 26 August.</>
        )}
      </p>
    </div>
  );
}

function Cell({ c }: { c: NomCell }) {
  if (c.state === 'sold') return <Sold c={c} />;
  if (c.state === 'open') return <Open c={c} />;
  if (c.state === 'passed') {
    return (
      <div className="ncell passed" title={`${c.code} — ${managerShort(c.nominator)} passed`}>
        <span className="nomby">{managerShort(c.nominator)}</span>
        <span className="bot"><em>passed</em><span className="code">{c.code}</span></span>
      </div>
    );
  }
  return (
    <div className="ncell upcoming" title={`${c.code} — ${managerShort(c.nominator)} nominates`}>
      <span className="nomby">{managerShort(c.nominator)}</span>
      <span className="code">{c.code}</span>
    </div>
  );
}

function Sold({ c }: { c: NomCell }) {
  const { last, first } = splitName(c.player ?? '');
  const fit = nameFit(last);
  // The tint is the WINNER's hue. See the note in `domain/farmAuction.ts`.
  const hue = hueFor(c.winner ?? '');
  const tint = `hsl(${hue} 62% 94%)`;
  const why = `${c.player} — ${managerShort(c.winner ?? '')} ${faab(c.bid ?? 0)}`
    + `, nominated by ${managerShort(c.nominator)} at ${c.code}`;
  return (
    <div className="ncell sold" style={{ background: tint, ['--hue' as string]: hue } as React.CSSProperties} title={why}>
      <span className="top">
        <span className={`pn ${fit}`}>
          <b>{last}</b>
          {first && fit !== 'wrap' && <i>{first}</i>}
        </span>
        {c.position && <span className={`poschip ident pos-${c.position}`}>{c.position}</span>}
      </span>
      <span className="bot">
        {c.winner && <span className="nm">{managerShort(c.winner)}</span>}
        {c.bid != null && <b className="bid">{faab(c.bid)}</b>}
        <span className="code">{c.code}</span>
      </span>
    </div>
  );
}

function Open({ c }: { c: NomCell }) {
  const { last } = splitName(c.player ?? '');
  return (
    <div className="ncell open"
      title={`${c.player} — up for bid, nominated by ${managerShort(c.nominator)}`}>
      <span className="wipe" aria-hidden="true" />
      <span className="top">
        <span className="dot" aria-hidden="true" />
        <span className="onwho">{last}</span>
      </span>
      <span className="lab">up for bid</span>
      <span className="code">{c.code}</span>
    </div>
  );
}
